import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import CurrentReportsSitrepCard from 'components/Reports/CurrentReportsSitrepCard';
import ReportVersionBar from 'components/Reports/ReportVersionBar';
import { ReportsEmptyState } from 'components/Reports/ReportsEmptyState';
import ReportsSitrepCard from 'components/Reports/ReportsSitrepCard';
import {
  SitrepCardSurface,
  type GetSitrepsResponse,
} from 'components/Reports/reports.types';
import {
  isInProgressSitrep,
  type ReportChain,
} from 'components/Reports/utils/reportChains';
import { useAorText } from 'utils/aorTerminology';

type ReportChainListProps = {
  scopeName: string;
  chains: ReportChain[];
  isLoading: boolean;
  isError: boolean;
  derivedAorId?: string | null;
  creating?: boolean;
  onCreate?: () => void;
};

const tipSurface = (tip: GetSitrepsResponse) => {
  if (tip.approval_status === 'PENDING') return SitrepCardSurface.Review;
  if (isInProgressSitrep(tip)) return SitrepCardSurface.Draft;
  return SitrepCardSurface.Current;
};

const ReportChainCard = ({ chain }: { chain: ReportChain }) => {
  const [viewingId, setViewingId] = useState<string | null>(null);
  const displayed =
    chain.versions.find((sitrep) => sitrep.id === viewingId) ?? chain.tip;
  const showingTip = displayed.id === chain.tip.id;
  const surface = tipSurface(chain.tip);

  const headerBanner = (
    <ReportVersionBar
      versions={chain.versions}
      tip={chain.tip}
      inProgress={chain.inProgress}
      viewingId={showingTip ? null : displayed.id}
      onViewVersion={setViewingId}
    />
  );

  return (
    <div className="tw:mb-4">
      {showingTip && surface !== SitrepCardSurface.Current ? (
        <ReportsSitrepCard
          sitrep={displayed}
          surface={surface}
          headerBanner={headerBanner}
        />
      ) : (
        <CurrentReportsSitrepCard
          sitrep={displayed}
          hideDraftButton={!showingTip}
          hasInProgressDraft={chain.inProgress != null}
          headerBanner={headerBanner}
        />
      )}
    </div>
  );
};

const ReportChainList = ({
  scopeName,
  chains,
  isLoading,
  isError,
  derivedAorId,
  creating = false,
  onCreate,
}: ReportChainListProps) => {
  const aorText = useAorText();
  if (isError) {
    return (
      <ReportsEmptyState
        icon="error"
        title="Error loading reports"
        description={aorText(
          'Try refreshing or selecting a different AOR or incident.'
        )}
      />
    );
  }

  if (isLoading && chains.length === 0) {
    return <Spinner className="tw:size-6" />;
  }

  if (chains.length === 0) {
    return (
      <div data-tour="reports-tabs">
        <ReportsEmptyState
          title={`No reports for ${scopeName} yet`}
          description={
            derivedAorId
              ? 'Create a draft report to get started.'
              : aorText(
                  'Unable to determine Area of Responsibility for this incident. Please ensure the incident is associated with an AOR in order to create and view draft SITREPs.'
                )
          }
        >
          {derivedAorId && onCreate ? (
            <Button
              type="button"
              data-tour="reports-create-draft"
              disabled={creating}
              onClick={onCreate}
            >
              <Plus />
              Create Draft Report
            </Button>
          ) : null}
        </ReportsEmptyState>
      </div>
    );
  }

  return (
    <div className="reports-tabs tw:pt-3">
      {chains.map((chain) => (
        <ReportChainCard key={chain.key} chain={chain} />
      ))}
    </div>
  );
};

export default ReportChainList;
